export function generatePrompt(formData) {
  const {
    promptType,
    gradeLevel,
    subject,
    customSubject,
    topic,
    learningObjectives,
    priorKnowledge,
    themeContext,
    additionalContext,
    timeConstraint,
    outputFormat,
    aiRole,
    negativePrompts,
    outputLanguages = ['English']
  } = formData;
  // If no significant data has been entered yet, return empty string
  if (!promptType && !gradeLevel && !subject && !topic) {
    return "";
  }
  // Get the actual subject value (handle custom subject)
  const actualSubject = subject === 'custom' ? customSubject : subject;
  let prompt = "";
  // 1. ALWAYS START WITH THE AI ROLE INSTRUCTION - NOW WITH SUBJECT/TOPIC SPECIFICITY
  if (aiRole) {
    // If we have a subject, make the role more specific
    if (actualSubject) {
      if (topic) {
        prompt += `# You are now acting as an ${getAiRoleText(aiRole)} specializing in ${actualSubject}${topic ? `, particularly in the area of ${topic}` : ''}\n\n`;
      } else {
        prompt += `# You are now acting as an ${getAiRoleText(aiRole)} specializing in ${actualSubject}\n\n`;
      }
    } else {
      prompt += `# You are now acting as an ${getAiRoleText(aiRole)}\n\n`;
    }
  } else {
    // No specific AI role selected, but we can still be specific with subject/topic
    if (actualSubject) {
      if (topic) {
        prompt += `# You are now acting as an experienced ${actualSubject} teacher and educational content expert with expertise in ${topic}\n\n`;
      } else {
        prompt += `# You are now acting as an experienced ${actualSubject} teacher and educational content expert\n\n`;
      }
    } else {
      prompt += "# You are now acting as an experienced teacher and educational content expert\n\n";
    }
  }
  // 2. CONTEXT ABOUT THE TEACHER AND THEIR NEEDS
  prompt += "I am a teacher";
  // Add grade level if provided
  if (gradeLevel) {
    prompt += ` working with ${getGradeLevelText(gradeLevel)} students`;
  }
  // Add subject if provided
  if (actualSubject) {
    prompt += gradeLevel ? ` on ${actualSubject}` : ` teaching ${actualSubject}`;
  }
  // Add topic if provided
  if (topic) {
    prompt += `, specifically focusing on ${topic}`;
  }
  // Close the first sentence
  prompt += ".\n\n";
  // 3. SPECIFIC REQUEST (Prompt Type)
  if (promptType) {
    prompt += `## Request\n${getPromptTypeSpecificText(promptType, topic, gradeLevel)}\n\n`;
  }
  // 4. LEARNING OBJECTIVES
  if (learningObjectives) {
    prompt += `## Learning Objectives\n${learningObjectives}\n\n`;
  }
  // 5. PRIOR KNOWLEDGE
  if (priorKnowledge) {
    prompt += `## Students' Prior Knowledge\n${priorKnowledge}\n\n`;
  }
  // 6. THEME OR CONTEXT
  if (themeContext) {
    prompt += `## Theme or Context\n${themeContext}\n\n`;
  }
  // 7. TIME CONSTRAINT
  if (timeConstraint) {
    prompt += `## Time Constraint\nThis should be designed for a ${getTimeConstraintText(timeConstraint)} session.\n\n`;
  }
  // 8. ADDITIONAL CONTEXT
  if (additionalContext) {
    prompt += `## Additional Context\n${additionalContext}\n\n`;
  }
  // 9. NEGATIVE PROMPTS
  if (negativePrompts && negativePrompts.length > 0) {
    prompt += "## Please Avoid\nIn your response, please avoid the following:\n";
    negativePrompts.forEach(item => {
      prompt += `- ${item}\n`;
    });
    prompt += "\n";
  }
  // 10. OUTPUT FORMAT
  if (outputFormat) {
    prompt += `## Output Format\nPlease format your response as ${getOutputFormatText(outputFormat)}.\n\n`;
  }
  // 11. OUTPUT LANGUAGES
  if (outputLanguages && outputLanguages.length > 0) {
    prompt += "## Output Languages\n";
    if (outputLanguages.length === 1) {
      if (outputLanguages[0] === 'English') {
        prompt += `Please provide your response in English UK.\n\n`;
      } else {
        prompt += `Please provide your response in ${outputLanguages[0]}.\n\n`;
      }
    } else {
      prompt += "Please provide your response in the following languages, with clear headings separating each language section:\n";
      outputLanguages.forEach(language => {
        if (language === 'English') {
          prompt += `- English UK\n`;
        } else {
          prompt += `- ${language}\n`;
        }
      });
      prompt += "\nPlease provide complete responses in each language (not just translations of headings).\n\n";
    }
  }
  // 12. FINAL INSTRUCTIONS (ALWAYS AT THE END)
  prompt += "## Prompt Rules\n";
  // Add pedagogical instructions
  prompt += "1. Use pedagogically sound approaches that align with best practices in education.\n";
  // Add age-appropriate content instruction with specific age reference if grade level is provided
  if (gradeLevel) {
    const ageInfo = getAgeAppropriateText(gradeLevel);
    prompt += `2. Create engaging, effective, and age-appropriate content suitable for ${ageInfo}.\n`;
  } else {
    prompt += "2. Create engaging, effective, and age-appropriate content for the specified audience.\n";
  }
  // Add concrete-pictorial-abstract progression instruction for appropriate grade levels
  if (gradeLevel && ['nursery', 'preK', 'kindergarten', 'grade1', 'grade2', 'grade3', 'grade4', 'grade5', 'grade6'].includes(gradeLevel)) {
    prompt += "3. Follow the concrete-pictorial-abstract progression in explanations and activities.\n";
  }
  // Add assessment variety instruction
  prompt += "4. Include varied assessment approaches (e.g., mini-whiteboards, hinge questions, exit tickets) where appropriate.\n";
  // Add differentiation instruction
  prompt += "5. Provide differentiation strategies to support all learners.\n";
  // Add language instruction
  if (outputLanguages.includes('English')) {
    prompt += "6. Use British English (UK) spelling and terminology throughout.\n";
  }
  return prompt;
}

function getGradeLevelText(gradeLevel) {
  switch (gradeLevel) {
    // Pre-School
    case 'nursery':
      return 'Nursery (2-3 years old)';
    case 'preK':
      return 'Pre-K (3-4 years old)';
    case 'kindergarten':
      return 'Reception (4-5 years old)';
    // Primary School
    case 'grade1':
      return 'Year 1 (KS1, 5-6 years old)';
    case 'grade2':
      return 'Year 2 (KS1, 6-7 years old)';
    case 'grade3':
      return 'Year 3 (KS2, 7-8 years old)';
    case 'grade4':
      return 'Year 4 (KS2, 8-9 years old)';
    case 'grade5':
      return 'Year 5 (KS2, 9-10 years old)';
    case 'grade6':
      return 'Year 6 (KS2, 10-11 years old)';
    // Secondary School
    case 'grade7':
      return 'Year 7 (KS3, 11-12 years old)';
    case 'grade8':
      return 'Year 8 (KS3, 12-13 years old)';
    case 'grade9':
      return 'Year 9 (KS3, 13-14 years old)';
    case 'grade10':
      return 'Year 10 (KS4, 14-15 years old)';
    case 'grade11':
      return 'Year 11 (KS4, 15-16 years old)';
    case 'grade12':
      return 'Year 12/13 (KS5, 16-18 years old)';
    // Higher Education
    case 'foundation':
      return 'Foundation Year students';
    case 'undergraduate':
      return 'Undergraduate students';
    case 'postgraduate':
      return 'Postgraduate students';
    // Other
    case 'vocational':
      return 'Vocational Training students';
    case 'adultEd':
      return 'Adult Education learners';
    case 'professionalDev':
      return 'Professional Development participants';
    // Legacy options
    case 'elementary':
      return 'primary school';
    case 'middle':
      return 'middle school';
    case 'highSchool':
      return 'secondary school';
    case 'college':
      return 'college/university';
    default:
      return 'students';
  }
}

function getTimeConstraintText(timeConstraint) {
  switch (timeConstraint) {
    case 'multiDay':
      return 'multi-day';
    case 'oneDay':
      return 'full day';
    case 'oneWeek':
      return 'one-week';
    case 'oneUnit':
      return 'complete teaching unit';
    case 'oneSemester':
      return 'full term';
    default:
      return timeConstraint;
  }
}

function getOutputFormatText(outputFormat) {
  switch (outputFormat) {
    case 'detailed':
      return 'a detailed narrative';
    case 'bullet':
      return 'bullet points';
    case 'stepByStep':
      return 'a step-by-step guide';
    case 'table':
      return 'a table or organised structure';
    case 'script':
      return 'a script or dialogue format';
    case 'presentation':
      return 'presentation slides with key points';
    case 'standardTemplate':
      return 'a standard educational format with clear sections';
    default:
      return outputFormat;
  }
}

function getAiRoleText(aiRole) {
  switch (aiRole) {
    case 'subjectExpert':
      return 'expert in the subject matter with deep knowledge and understanding';
    case 'teacher':
      return 'experienced teacher with classroom expertise and pedagogical knowledge';
    case 'tutor':
      return 'personal tutor focused on individualised learning and student engagement';
    case 'curriculumDesigner':
      return 'curriculum designer with expertise in creating cohesive educational programmes';
    case 'educationalPsychologist':
      return 'educational psychologist with knowledge of learning processes and development';
    case 'assessmentSpecialist':
      return 'assessment specialist focused on measuring and evaluating student learning';
    case 'contentCreator':
      return 'educational content creator skilled at developing engaging learning materials';
    case 'simplifier':
      return 'expert at simplifying complex concepts for easier understanding';
    default:
      return 'educational expert';
  }
}

function getAgeAppropriateText(gradeLevel) {
  // Return more specific age-appropriate guidance based on grade level
  switch (gradeLevel) {
    // Pre-School
    case 'nursery':
    case 'preK':
    case 'kindergarten':
      return 'young children aged 2-5 years who learn best through play, simple language, and concrete examples';
    // Primary School Lower
    case 'grade1':
    case 'grade2':
      return 'children aged 5-7 years who are developing basic literacy and numeracy skills and respond well to visual aids and hands-on activities';
    // Primary School Middle
    case 'grade3':
    case 'grade4':
      return 'children aged 7-9 years who are developing more complex thinking skills and benefit from concrete examples before abstract concepts';
    // Primary School Upper
    case 'grade5':
    case 'grade6':
      return 'children aged 9-11 years who are developing more sophisticated thinking skills and can handle more abstract concepts when presented concretely';
    // Secondary School Lower
    case 'grade7':
    case 'grade8':
    case 'grade9':
      return 'young people aged 11-14 years who are developing abstract thinking skills and benefit from content that connects to their personal experiences';
    // Secondary School Upper
    case 'grade10':
    case 'grade11':
      return 'young people aged 14-16 years who are preparing for GCSEs and are capable of complex abstract thinking and critical analysis';
    case 'grade12':
      return 'young people aged 16-18 years who are studying A-Levels or equivalent qualifications and require higher-order thinking skills';
    // Higher Education
    case 'foundation':
    case 'undergraduate':
    case 'postgraduate':
      return 'adult learners who require sophisticated content with real-world applications and professional relevance';
    // Other
    case 'vocational':
    case 'adultEd':
    case 'professionalDev':
      return 'adult learners with diverse backgrounds who need practical, applicable content with clear professional relevance';
    default:
      return 'learners of the specified age/year group';
  }
}

function getPromptTypeSpecificText(promptType, topic, gradeLevel) {
  const topicText = topic || "this subject";
  const gradeLevelText = getGradeLevelText(gradeLevel);
  switch (promptType) {
    case 'lessonPlan':
      return `I need help creating a comprehensive lesson plan about ${topicText}. Please include learning objectives, necessary materials, a starter activity, main teaching components, student practice opportunities, assessment strategies, and plenary/reflection.`;
    case 'assessment':
      return `I need to create an assessment on ${topicText}. Please help me design a balanced assessment that includes different question types (multiple choice, short answer, extended response, etc.) that measure different levels of understanding. Include a mark scheme or scoring guide where appropriate.`;
    case 'studentFeedback':
      return `I need help crafting constructive feedback for my students about their work on ${topicText}. Please provide templates or examples of feedback that is specific, actionable, and encouraging for different performance levels.`;
    case 'differentiation':
      return `I need strategies to differentiate my teaching on ${topicText} to meet diverse learning needs in my classroom. Please suggest modifications for students who need additional support, as well as extensions for more able learners.`;
    case 'classroomManagement':
      return `I need classroom management strategies that would work well during activities related to ${topicText}. Please suggest approaches for maintaining engagement, handling transitions, and addressing potential behavioural issues.`;
    case 'contentCreation':
      return `I need help creating educational content about ${topicText}. Please suggest creative and engaging ways to present this information, possibly including multimedia elements, interactive components, or connections to real-world applications.`;
    case 'simplification':
      return `I need help simplifying the complex topic of ${topicText} for my ${gradeLevelText} students. Please provide analogies, metaphors, or simplified explanations that maintain accuracy while making the concept more accessible.`;
    case 'activities':
      return `I need engaging activities to help my students learn about ${topicText}. Please suggest interactive, hands-on, or collaborative activities that will promote active learning and deeper understanding of this topic.`;
    case 'curriculumStandards':
      return `I need to ensure my teaching of ${topicText} aligns with curriculum standards. Please help me understand how to effectively address the key standards while making the content engaging and meaningful for my students.`;
    default:
      return `I need educational resources related to ${topicText} for my students.`;
  }
}